// Character authoring draft store.
//
// The wizard / material editor flow: user edits → in-memory draft updates →
// UI renders the draft → user clicks Save → server PATCH/POST → draft clears.
//
// The localStorage write-through lets the user reload mid-edit without losing
// work, but every draft is **tagged with the API_TARGET it was authored
// against**. On load, a draft whose tag doesn't match the current backend is
// surfaced as `stale-backend` (not silently merged) so switching local ↔
// remote can't corrupt the avatar. See docs/UX_PRINCIPLES.md.

import { API_TARGET } from './devServer'
import { getCurrentUserId } from './userScope'
import { classifyStoredDraft } from './draftBackendTag'

// Drafts are stored per-character — `aos-character-authoring-v4-<charId>`.
// The previous v3 single-key design caused myra-overwritten-by-luna bug:
// editing one character then syncing while another was active sent the
// stale draft as the active character's record. Per-character keys make
// drafts inherently scoped to their owner.
const LS_DRAFT_BASE = 'aos-character-authoring-v4'
function lsKeyFor(charId) {
  // Scoped by user id (ENG-559) then charId: aos-character-authoring-v4-{uid}-{charId}.
  return charId ? `${LS_DRAFT_BASE}-${getCurrentUserId() ?? 'anon'}-${charId}` : null
}

// Tracks which character the in-memory `_draft` belongs to. setActiveCharacter()
// swaps the in-memory draft when the active character changes.
let _activeCharId = null

// `_draftDirty` is the unsaved-changes signal, **separate from** the draft
// data. The draft itself is the Material Editor's working copy and stays
// populated even after a successful sync (so the editor doesn't go blank).
// Dirty flips false on a successful Sync to Cloud via markDraftSaved(),
// flips true on every updateDraft() call. Persisted to localStorage so the
// pill state survives page reloads. See docs/UX_PRINCIPLES.md.
let _draftDirty = false

// Flat slot structure: each materials key is a mesh role (body, face,
// outfit, shoes, eyes, teeth, hair). Channels under each role use flat
// names (diffuse, normal, roughness, thickness, orm, alpha, irisL, irisR,
// scleraL, scleraR). No `maps` wrapper.

export const DEFAULT_MESH_SLOTS = {
  'body':       ['body', 'neck', 'torso', '^arm', 'leg', 'hand', 'foot'],
  'face':       ['head', 'face'],
  'outfit':     ['sweater', 'shirt', 'top', 'jeans', 'pants', 'trouser'],
  'shoes':      ['shoe', 'sneaker', 'skate', 'wheel'],
  'eyes.left':  ['eye_?l(?:eft)?', 'irisl', 'scleral', 'leye'],
  'eyes.right': ['eye_?r(?:ight)?', 'irisr', 'scleraR', 'reye'],
  'teeth':      ['teeth', 'tooth', 'gum', 'saliva'],
  'hair':       ['^hair', 'scalp'],
  'hidden':     ['eyebrow', 'eyelash', 'babyhair', 'peachfuzz', 'eyeshell'],
}

const BLANK_TEMPLATE = {
  id: '',
  label: '',
  rigPath: null,
  material: { kind: 'pbr', color: '#c8a882' },
  faceMorphs: true,
  materials: {
    body:   { kind: 'skin', color: '#c8a882' },
    face:   { kind: 'skin', color: '#c8a882' },
    outfit: { kind: 'pbr' },
    shoes:  { kind: 'pbr' },
    eyes:   { kind: 'eye' },
    teeth:  { kind: 'pbr' },
    hair:   { kind: 'hair', color: '#3a2620' },
  },
  meshSlots: { ...DEFAULT_MESH_SLOTS },
}

export function createBlankCharacter(id, label, shaderKind = 'pbr') {
  return {
    ...JSON.parse(JSON.stringify(BLANK_TEMPLATE)),
    id,
    label,
    material: { kind: shaderKind, color: '#c8a882' },
  }
}

let _draft = null
let _listeners = []
let _staleBackendDraft = null // draft from another backend, held but not auto-applied
let _pendingRigFile = null // File object held in memory for upload on Save
const _pendingTextureFiles = {} // "role.mapKey" → File, for retry during Sync

export function setPendingRigFile(file) { _pendingRigFile = file }
export function getPendingRigFile() { return _pendingRigFile }
export function clearPendingRigFile() { _pendingRigFile = null }

export function setPendingTextureFile(slotKey, file) { _pendingTextureFiles[slotKey] = file }
export function clearPendingTextureFile(slotKey) { delete _pendingTextureFiles[slotKey] }
export function clearAllPendingTextureFiles() { Object.keys(_pendingTextureFiles).forEach(k => delete _pendingTextureFiles[k]) }
export function getPendingTextureFiles() { return { ..._pendingTextureFiles } }

function notify() {
  for (const fn of _listeners) fn(_draft)
}

function persist() {
  if (!_draft) return
  // Persist under the draft's own id, falling back to the active char id.
  // This avoids ever writing draft data under the wrong character's key.
  const charId = _draft.id || _activeCharId
  const key = lsKeyFor(charId)
  if (!key) return
  try {
    const wrapped = {
      _authoredAgainst: API_TARGET,
      _savedAt: Date.now(),
      _isDirty: _draftDirty,
      draft: _draft,
    }
    localStorage.setItem(key, JSON.stringify(wrapped))
  } catch { /* ignore */ }
}

export function getDraft() {
  return _draft
}

// Returns true when there's a draft for the active character AND the draft
// has unsaved local edits (the user changed something that hasn't been
// pushed to the server yet). After a successful Sync to Cloud, the draft
// stays populated (so the Material Editor keeps working) but this returns
// false (so the dirty pill and switch-confirmation modal stay silent).
export function hasActiveDraft() {
  return !!_draft && _draftDirty && _draft.id === _activeCharId
}

// Returns true if there's an active in-memory draft for the active
// character regardless of dirty state. Use this for "is the editor
// initialized?" questions, NOT for "are there unsaved changes?" — that's
// hasActiveDraft().
export function hasActiveDraftData() {
  return !!_draft && _draft.id === _activeCharId
}

// Mark the current draft as saved — the server now holds exactly this
// state. Keeps `_draft` populated (the Material Editor needs the data to
// render) but flips `_draftDirty` to false so the dirty UI clears. Called
// from BlueprintEditor.saveToCloud on full success.
export function markDraftSaved() {
  if (!_draft) return
  _draftDirty = false
  persist()
  notify()
}

// Returns the active character id the draft store is currently scoped to.
export function getActiveCharacterId() {
  return _activeCharId
}

// Switch the active character. Persists the current in-memory draft to its
// own per-character key first, then loads (or clears) the draft for the
// new character. Call this whenever App's active character changes — on
// initial load and on Character menu switches.
export function setActiveCharacter(charId) {
  if (charId === _activeCharId) return
  // Flush current draft to its own key.
  if (_draft) persist()
  _activeCharId = charId
  // Hydrate or clear the in-memory draft for the new character.
  _draft = null
  _draftDirty = false
  _staleBackendDraft = null
  if (charId) loadDraftFromLS()
  notify()
}

// Returns a stale-backend draft (authored against a different API_TARGET)
// for UI display + Apply/Discard prompts. Null when no such draft exists.
export function getStaleBackendDraft() {
  return _staleBackendDraft
}

export function clearStaleBackendDraft() {
  _staleBackendDraft = null
  const key = lsKeyFor(_activeCharId)
  if (key) {
    try { localStorage.removeItem(key) } catch { /* ignore */ }
  }
}

// Promote the stale-backend draft to an active draft against the current
// backend. Used when the user explicitly chooses to apply it anyway.
// The promoted draft is treated as DIRTY by definition — it hasn't been
// saved against this backend yet.
export function applyStaleBackendDraft() {
  if (!_staleBackendDraft) return
  _draft = JSON.parse(JSON.stringify(_staleBackendDraft))
  _draftDirty = true
  _staleBackendDraft = null
  persist()
  notify()
}

// initDraft hydrates from a server character record (or a blank template
// for the wizard). The freshly-hydrated draft is CLEAN — it matches the
// server. _draftDirty stays false until the user actually edits something
// via updateDraft.
export function initDraft(base = null) {
  if (base) {
    _draft = JSON.parse(JSON.stringify(base))
  } else {
    _draft = JSON.parse(JSON.stringify(BLANK_TEMPLATE))
  }
  _draftDirty = false
  persist()
  notify()
}

// updateDraft applies a user-initiated edit and sets the dirty flag. Every
// call here means "the user changed something" — the dirty pill should
// appear and the unsaved-changes modal should fire on the next character
// switch.
export function updateDraft(patch) {
  if (!_draft) _draft = JSON.parse(JSON.stringify(BLANK_TEMPLATE))
  deepMerge(_draft, patch)
  _draftDirty = true
  persist()
  notify()
}

// clearDraft fully discards the draft (data + dirty flag). Use ONLY for
// explicit user-driven discard actions (Discard in the unsaved-changes
// modal, character deletion). For "we just saved successfully", use
// markDraftSaved() instead — it keeps the editor's working copy populated.
export function clearDraft() {
  _draft = null
  _draftDirty = false
  const key = lsKeyFor(_activeCharId)
  if (key) {
    try { localStorage.removeItem(key) } catch { /* ignore */ }
  }
  try { localStorage.removeItem('aos-character-authoring-draft-v1') } catch { /* ignore */ }
  // Also nuke the legacy single-key storage so it can never resurrect.
  try { localStorage.removeItem('aos-character-authoring-v3') } catch { /* ignore */ }
  notify()
}

export function subscribe(fn) {
  _listeners.push(fn)
}

export function unsubscribe(fn) {
  _listeners = _listeners.filter(f => f !== fn)
}

// Loads the draft for the currently-active character (set via
// setActiveCharacter). Returns:
//   'loaded'        → _draft is populated against the current backend
//   'stale-backend' → a draft exists but was authored against a different
//                     backend; available via getStaleBackendDraft()
//   'none'          → no draft for this character in localStorage
//
// Per-character storage key — switching characters never resurrects
// another character's draft.
export function loadDraftFromLS() {
  const key = lsKeyFor(_activeCharId)
  if (!key) return 'none'
  try {
    const raw = localStorage.getItem(key)
    const res = classifyStoredDraft(raw, API_TARGET, _activeCharId)
    switch (res.status) {
      case 'loaded':
        _draft = res.draft
        _draftDirty = res.dirty
        return 'loaded'
      case 'stale-backend':
        _staleBackendDraft = res.draft
        console.warn(
          `[CharacterAuthoring] Draft for ${_activeCharId} was authored against ` +
          `${res.authoredAgainst ?? 'an untagged legacy store'} but current backend is ${API_TARGET}. Held aside as stale-backend.`
        )
        return 'stale-backend'
      default:
        if (res.corrupted) {
          console.warn(
            `[CharacterAuthoring] Draft under key for "${_activeCharId}" has a mismatched character id — discarding.`
          )
          try { localStorage.removeItem(key) } catch { /* ignore */ }
        }
        return 'none'
    }
  } catch { /* ignore */ }
  return 'none'
}

function deepMerge(target, source) {
  for (const key of Object.keys(source)) {
    if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])
        && typeof target[key] === 'object' && target[key] !== null) {
      deepMerge(target[key], source[key])
    } else {
      target[key] = source[key]
    }
  }
}

// Called after rig upload resolves, to keep the in-memory draft in sync with the real S3 URL
export function syncDraftToCharacterStore(patch) {
  if (!_draft) return
  deepMerge(_draft, patch)
  persist()
  notify()
}
